import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import {
  ICartMeta,
  IQuoteCartAddServicePayload,
  IQuoteCartSubmitPayload,
  IQuoteCreatedResponse,
  IServiceQuoteCart,
} from '~/purchase/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IServiceQuoteCart, ICartMeta>,
      IErrorApiResponse
    >
  >;
  addService?: UseMutationOptions<
    ISuccessApiResponse<IServiceQuoteCart>,
    IErrorApiResponse,
    IQuoteCartAddServicePayload
  >;
  updateServicePrices?: UseMutationOptions<
    ISuccessApiResponse<IServiceQuoteCart>,
    IErrorApiResponse,
    { services: Array<{ id: string; price: number }> }
  >;
  removeService?: UseMutationOptions<
    ISuccessApiResponse<IServiceQuoteCart>,
    IErrorApiResponse,
    string
  >;
  submit?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCreatedResponse>,
    IErrorApiResponse,
    IQuoteCartSubmitPayload
  >;
  reset?: UseMutationOptions<void, IErrorApiResponse, void>;
};

export const useManageServiceQuoteCart = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IServiceQuoteCart, ICartMeta>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(idCustomer) && options.get?.enabled,
  });

  const addServiceMutation = useMutation({
    mutationFn: (body) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IServiceQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart/services`,
            {
              body: JSON.stringify(body),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    ],
    ...options.addService,
  });

  const updateServicePricesMutation = useMutation({
    mutationFn: (body) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IServiceQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart/services`,
            {
              body: JSON.stringify(body),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    ],
    ...options.updateServicePrices,
  });

  const removeServiceMutation = useMutation({
    mutationFn: (id) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IServiceQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart/services/${id}`,
            {
              method: 'DELETE',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    ],
    ...options.removeService,
  });

  const submitMutation = useMutation({
    mutationFn: (body) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCreatedResponse>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart/submit`,
            {
              body: JSON.stringify(body),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    mutationKey: PurchaseQueryKeys.serviceQuoteCart.submit(idCustomer),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    ],
    ...options.submit,
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      idCustomer
        ? callCoreApi<void>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/service-quotes/cart`,
            { method: 'DELETE' },
          )
        : emptyQueryError(),
    mutationKey: PurchaseQueryKeys.serviceQuoteCart.reset(idCustomer),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.serviceQuoteCart.base(idCustomer),
    ],
    ...options.reset,
  });

  return restrictApi(options, {
    get: getQuery,
    addService: addServiceMutation,
    updateServicePrices: updateServicePricesMutation,
    removeService: removeServiceMutation,
    submit: submitMutation,
    reset: resetMutation,
  });
};
